// @ts-nocheck -- Transitional typing for a legacy JavaScript module.
import { useState } from "react";
import { FileText, FolderOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import SearchField from "@/components/common/SearchField";
import StatusBadge from "@/components/common/StatusBadge";
import EmptyState from "@/components/common/EmptyState";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { useDeleteDocument } from "@/hooks/useDeals";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AUDIENCE_LABELS } from "@/constant/deals.constant";
import { formatFileSize, formatUtcDate } from "@/utils/common.utils";

const LEGACY_AUDIENCE = { internal: "internal", shared_with_externals: "externals", client_visible: "client" };

export default function DocumentsTab({ docs, deal, onOpen, onAddDocument, onChangeAudience }) {
  const [search, setSearch] = useState("");
  const [deleting, setDeleting] = useState(null);
  const { mutateAsync: deleteDocument } = useDeleteDocument();

  const q = search.trim().toLowerCase();
  const visibleDocs = docs.filter((d) => !q || [d.name, d.category].some((v) => String(v || "").toLowerCase().includes(q)));

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchField className="w-full sm:min-w-[280px] sm:flex-1" value={search} onChange={(e) => setSearch(e.target.value)} onClear={() => setSearch("")} placeholder="Search documents…" />
        <Button type="button" variant="dashboardPrimary" size="lg" onClick={onAddDocument} className="h-11 w-full !rounded-xl sm:ml-auto sm:w-auto" data-testid="add-doc-btn">Upload document</Button>
      </div>

      {!visibleDocs.length ? (
        <EmptyState compact icon={FolderOpen} title="No documents yet" description="Upload contracts, disclosures, and other transaction files here." className="border border-edge" data-testid="documents-empty" />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-edge bg-white" role="region" aria-label="Documents table" tabIndex={0}>
          <table className="w-full min-w-[860px] text-left text-sm">
            <thead>
              <tr className="border-b border-edge text-[11px] uppercase tracking-wider text-stone">
                <th className="px-4 py-3 font-medium">File name</th>
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Uploaded by</th>
                <th className="px-4 py-3 font-medium">Visible to</th>
                <th className="px-4 py-3 font-medium">Updated</th>
                <th className="px-4 py-3 font-medium">Size</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-edge">
              {visibleDocs.map((document) => {
                const canOpen = document.storage_path || document.url;
                return (
                  <tr key={document.id} className="hover:bg-bone/40" data-testid={`doc-row-${document.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <FileText className="size-4 shrink-0 text-stone" />
                        {canOpen ? (
                          <Button variant="seamless" type="button" onClick={() => onOpen(document)} className="truncate font-medium text-ink hover:text-moss" data-testid={`doc-link-${document.id}`}>{document.name}</Button>
                        ) : (
                          <span className="truncate font-medium text-ink">{document.name}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-stone">{document.category || "—"}</td>
                    <td className="px-4 py-3"><StatusBadge tone="success" className="px-2 py-0.5 text-xs font-medium">{document.storage_path ? "Uploaded" : "Linked"}</StatusBadge></td>
                    <td className="px-4 py-3 text-stone">{document.uploaded_by || "—"}</td>
                    <td className="px-4 py-3">
                      <Select
                        value={document.audience || LEGACY_AUDIENCE[document.visibility] || "internal"}
                        onValueChange={(audience) => onChangeAudience?.(document, audience)}
                      >
                        <SelectTrigger className="h-9 w-[210px] text-left text-sm text-stone" aria-label={`Who can see ${document.name}`} data-testid={`doc-audience-${document.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {Object.entries(AUDIENCE_LABELS).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 text-stone">{formatUtcDate(document.created_at, {}, "—")}</td>
                    <td className="px-4 py-3 text-stone">{formatFileSize(document.size)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-4">
                        {canOpen && <Button variant="seamless" type="button" onClick={() => onOpen(document)} className="text-sm font-medium text-moss hover:underline">Preview</Button>}
                        <Button variant="seamless" type="button" onClick={() => setDeleting(document)} className="text-sm font-medium text-stone hover:text-rust" aria-label={`Delete ${document.name}`} data-testid={`doc-delete-${document.id}`}>Delete</Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {deleting && (
        <ConfirmDialog
          open
          onOpenChange={(next) => { if (!next) setDeleting(null); }}
          title={`Delete ${deleting.name}?`}
          description="It will be removed from this deal for everyone who can see it."
          confirmLabel="Delete"
          busyLabel="Deleting…"
          onConfirm={async () => {
            // The mutation toasts its own error; close either way.
            await deleteDocument({ docId: deleting.id, dealId: deal.id }).catch(() => {});
            setDeleting(null);
          }}
          testId="doc-delete-confirm"
        />
      )}
    </div>
  );
}
